import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import logo from "./../wwwroot/content/images/AMBUschema-logo.png";
import ReactSvgIcon from "./common/ReactSvgIcon";
import LoginOutModalForm from './user/LoginOutModalForm';
import {
  useShowLoginModal,
  useHideHeader,
  useHideHeaderButton,
  useUserInfo,
  useIsAuthorized,
  useImpersonatedUserId,
  useLoading,
  useStation
} from './contexts/GlobalStateContext';
import { fetchWithAuth } from './services/api';

const Frame = ({ children }) => {
  const { showLoginModal, setShowLoginModal } = useShowLoginModal();
  const { hideHeader, setHideHeader } = useHideHeader();
  const { hideHeaderButton, setHideHeaderButton } = useHideHeaderButton();
  const { userInfo, setUserInfo } = useUserInfo();
  const { loading, setLoading } = useLoading();
  const { impersonatedUserId, setImpersonatedUserId } = useImpersonatedUserId();
  const { isAuthorized, setIsAuthorized } = useIsAuthorized();
  const isSystemAdmin = userInfo?.role === 'SystemAdmin';
  const isAdmin = userInfo?.role === 'Admin';
  const isStandard = userInfo?.role === 'Standard';
  const isImpersonated = !!impersonatedUserId;

  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captionText, setCaptionText] = useState('Logga in');

  const { setStation } = useStation();

  const menuRefs = {
    homeLi: useRef(null),
    roleLi: useRef(null),
    dokumentLi: useRef(null),
    userLi: useRef(null),
    schemanLi: useRef(null),
    schemanVoLi: useRef(null),
    excelLi: useRef(null),
    dashboardLi: useRef(null),
    statistikLi: useRef(null),
  }

  // Kontrollera om användare redan är inloggad vid start
  // 1. Initial token-kontroll (körs bara en gång vid mount)
  useEffect(() => {
    const token = sessionStorage.getItem('jwt_token');

    if (token && !isAuthorized) {
      // ← LÄGG TILL: kolla om det är ett PublicViewer-token vid ?view=public
      const params = new URLSearchParams(window.location.search);
      if (params.get('view') === 'public') {
        sessionStorage.clear(); // Rensa gammalt token — hämta nytt nedan
        return; // låt useEffect #2 hantera inloggningen via handleLinkViewSchemas
      }

      try {
        setIsAuthorized(true);
        const payload = JSON.parse(atob(token.split('.')[1]));
        setTimeout(() => {
          setUserInfo({
            username: payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] || payload.name || "Schemavisare",
            role: payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || payload.role || "Standard",
            isPublicViewer: payload.IsPublicViewer === "true",
            userId: payload.ActualUserId
          });
          setImpersonatedUserId("")
        }, 20);

      } catch (error) {
        console.error("Could not parse token:", error);
        sessionStorage.clear();
      }
    }
  }, []); // Bara vid mount

  // Hantera ENDAST public access autentisering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autoView = params.get('view');

    if (autoView === 'public' && !isAuthorized) {
      sessionStorage.clear();   // ← alltid rensa först
      handleLinkViewSchemas();
    }
  }, [location.search]); // Ta bort isAuthorized som dependency för att undvika loopar

  // 3. Navigering och access-kontroll
  useEffect(() => {
    if (!isAuthorized) {
      navigate("/", { state: location.state });
      return;
    }

    // Public viewer restrictions
    if (userInfo?.isPublicViewer) {
      const currentPath = location.pathname;
      const allowedPaths = ['/scheman'];

      if (!allowedPaths.includes(currentPath)) {
        navigate("/scheman");
      }
    }

  }, [isAuthorized, userInfo?.isPublicViewer, location.pathname]);

  // 4. Header-visning baserat på roll
  useEffect(() => {
    if (!isAuthorized) {
      setHideHeader(true);
      setHideHeaderButton(true);
      return;
    }

    setTimeout(() => {
      const isStandardUser = userInfo?.role === 'Standard';
      setHideHeader(isStandardUser);
      setHideHeaderButton(isStandardUser);
    }, 20)
  }, [isAuthorized, userInfo?.role]);


  useEffect(() => {
    if (captionText === 'Fel användarnamn eller lösenord' || captionText.includes('misslyckades')) {
      const timer = setTimeout(() => {
        setCaptionText('Logga in');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [captionText]);


  // Funktion för public link access
  const handleLinkViewSchemas = async () => {
    try {
      const response = await fetch("/api/public/link-view-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Public access failed");

      const data = await response.json();
      sessionStorage.setItem("jwt_token", data.token);
      sessionStorage.setItem('user_id', data.userId);
      sessionStorage.setItem('username', data.username);
      sessionStorage.setItem('user_role', data.role || 'Standard');
      setUserInfo({
        username: data.username,
        role: data.role,
        userId: data.userId,
        isPublicViewer: true,
      });
      setIsAuthorized(true);
      setTimeout(() => {
        navigate("/scheman" + location.search);
      }, 20);

    } catch (error) {
      console.error("Public access error:", error);
    }
  };

  // Funktion för public Login link access
  const handleViewSchemas = async () => {
    try {
      const response = await fetch("/api/public/view-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Public access failed");

      const data = await response.json();
      sessionStorage.setItem("jwt_token", data.token);
      sessionStorage.setItem('user_id', data.userId);
      sessionStorage.setItem('username', data.username);
      sessionStorage.setItem('user_role', data.role || 'Standard');
      setUserInfo({
        username: data.username,
        role: data.role,
        userId: data.userId,
        isPublicViewer: true,
      });
      setIsAuthorized(true);
      setTimeout(() => {
        navigate("/scheman" + location.search);
      }, 20);

    } catch (error) {
      console.error("Public access error:", error);
    }
  };

  const openLoginModal = (e) => {
    e.preventDefault();
    setShowLoginModal(true);
  }

  const checkPasswordOnServerAsync = async (password, username) => {
    try {
      const response = await fetch('/api/authorization/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Login error:', error);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  };

  const checkPasswordAsync = async (password, username) => {
    setLoading(true);
    try {
      const response = await checkPasswordOnServerAsync(password, username);

      if (response && response.token) {
        // 🟢 Spara i sessionStorage istället för sessionStorage
        sessionStorage.setItem('jwt_token', response.token);
        sessionStorage.setItem('user_id', response.userId);
        sessionStorage.setItem('username', response.username);
        sessionStorage.setItem('user_role', response.role || 'Standard');

        // Uppdatera state
        setIsAuthorized(true);

        setUserInfo({
          userId: response.userId,
          username: response.username,
          role: response.role || 'Standard',
        });

        setShowLoginModal(false);
        setCaptionText('Logga in');
        setUsername('');
        setPassword('');
        if (response.role === "SystemAdmin") {
          navigate("/dashboard");
        } else
          if (response.role === "Standard") {
            navigate("/scheman");
          } else {
            navigate('/');
          }

        return 'PasswordOk';
      } else {
        setCaptionText('Fel användarnamn eller lösenord');
        return 'PasswordIncorrect';
      }
    } catch (error) {
      console.error(error);
      setCaptionText('Inloggning misslyckades. Försök igen.');
      return 'PasswordIncorrect';
    } finally {
      setLoading(false);
    }
  };

  const logOutUserAsync = async () => {
    const response = await fetchWithAuth('/api/authorization/logout', {
      method: 'POST',
    });

    if (!response.ok) throw new Error('Logout failed');

    return await response.json();
  };

  const logOutAsync = async () => {
    setLoading(true);
    try {
      await logOutUserAsync();

      if (isStandard) {
        setStation("---");
      }

      setImpersonatedUserId("");
      setUserInfo(null);
      sessionStorage.clear();

      setTimeout(() => {
        setIsAuthorized(false);
      }, 20);

      return 'userLoggedOut';
    } catch (error) {
      console.error(error);
      sessionStorage.clear();
      setIsAuthorized(false);
      setUserInfo(null);
      return 'Error';
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setCaptionText('Logga in');
    setShowLoginModal(false);
  };

  const handleLogInOut = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      if (isAuthorized) {
        const response = await logOutAsync();
        if (response === 'userLoggedOut' || response === 'userAlreadyLoggedOut') {
          handleClose();
          navigate('/');
        }
      } else {
        if (!username.trim()) {
          setCaptionText('Ange användarnamn');
          setLoading(false);
          return;
        }
        if (!password) {
          setCaptionText('Ange lösenord');
          setLoading(false);
          return;
        }

        const response = await checkPasswordAsync(password, username);
        if (response === 'PasswordOk') {

          handleClose();
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setCaptionText('Ett fel uppstod. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {(!hideHeaderButton && userInfo?.role !== 'Standard' && <button
        onClick={() => setHideHeader(!hideHeader)}
        className="md:tw-block tw-hidden fullscreen-toggle-btn tw-p-2 tw-bg-white tw-bg-opacity-90 tw-rounded-md tw-shadow hover:tw-bg-opacity-100 tw-transition-all"
      >
        {hideHeader ? (
          <svg className="tw-w-5 tw-h-5 tw-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        ) : (
          <svg className="tw-w-5 tw-h-5 tw-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15v4.5M15 15h4.5M15 15l5.25 5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25" />
          </svg>
        )}
      </button>)}

      {!hideHeader && userInfo?.role !== 'Standard' && (
        <header className="tw-w-full tw-bg-gray-300" style={{ backgroundColor: 'lightgrey', height: '81px', margin: '0' }}>
          <div className="tw-max-w-7xl tw-mx-auto tw-pl-48 tw-h-full">
            <div className="tw-flex tw-items-center tw-h-full">
              {isAuthorized && (
                <NavLink className="tw-flex-shrink-0 tw-hidden md:tw-block" style={{ marginLeft: '-40px' }} to="/">
                  <ReactSvgIcon icon={logo} iconClass={'logo'} />
                </NavLink>
              )}

              <div className="tw-flex-1 tw-flex tw-justify-between tw-items-center tw-h-full">
                {isAuthorized && (
                  <>
                    {/* Layout för vanlig Admin ELLER impersonated SystemAdmin */}
                    {(!isSystemAdmin || isImpersonated) && (
                      <>
                        <ul className="tw-flex tw-list-none spriteTabs">
                          {/* Huvudmenyer - visa om isAdmin eller isImpersonated */}
                          {(isAdmin || isImpersonated) && (
                            <>
                              <li ref={menuRefs.homeLi}>
                                <NavLink to="/" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Hem<span />
                                </NavLink>
                              </li>
                              <li ref={menuRefs.statistikLi}>
                                <NavLink to="/statistik" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Produktionsdata<span />
                                </NavLink>
                              </li>
                              <li ref={menuRefs.roleLi}>
                                <NavLink to="/personal" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Personal<span />
                                </NavLink>
                              </li>
                              <li ref={menuRefs.dokumentLi}>
                                <NavLink to="/dokument" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Dokument<span />
                                </NavLink>
                              </li>
                            </>
                          )}

                          <li ref={menuRefs.schemanLi}>
                            <NavLink to="/scheman" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                              &nbsp;&nbsp;Scheman<span />
                            </NavLink>
                          </li>

                          <li ref={menuRefs.schemanVoLi}>
                            <NavLink to="/scheman-vo" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                              &nbsp;&nbsp;Scheman&nbsp;VO<span />
                            </NavLink>
                          </li>

                          {(isAdmin || isImpersonated) && (
                            <li ref={menuRefs.excelLi}>
                              <NavLink to="/excel" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                &nbsp;&nbsp;Excel<span />
                              </NavLink>
                            </li>
                          )}

                          <li>
                            <span className="tw-inline-block tw-px-4 tw-font-black tw-whitespace-nowrap">
                              &nbsp;&nbsp;&nbsp;&nbsp;{userInfo?.username || ""}
                            </span>
                          </li>

                          <li>
                            <NavLink to="/user" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600" onClick={openLoginModal}>
                              <span></span>&nbsp;&nbsp;<i className="fas fa-user"></i><span />
                            </NavLink>
                          </li>

                          {isSystemAdmin && (
                            <>
                              <li ref={menuRefs.dashboardLi}>
                                <NavLink to="/dashboard" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Admin<span />
                                </NavLink>
                              </li>
                              <li>
                                <NavLink to="/vo" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                                  &nbsp;&nbsp;Verksamhetsområden<span />
                                </NavLink>
                              </li>
                            </>
                          )}
                        </ul>
                      </>
                    )}

                    {/* Special layout för ren SystemAdmin (inte impersonated) */}
                    {(isSystemAdmin && !isImpersonated) && (
                      <ul className="tw-flex tw-list-none spriteTabs">
                        <li ref={menuRefs.dashboardLi}>
                          <NavLink to="/dashboard" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                            &nbsp;&nbsp;Admin<span />
                          </NavLink>
                        </li>
                        <li>
                          <NavLink to="/vo" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600">
                            &nbsp;&nbsp;Verksamhetsområden<span />
                          </NavLink>
                        </li>
                        <li>
                          <span className="tw-inline-block tw-px-4 tw-font-black tw-whitespace-nowrap">
                            &nbsp;&nbsp;&nbsp;&nbsp;{userInfo?.username || ""}&nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                        </li>
                        <li>
                          <NavLink to="/user" className="tw-inline-block tw-px-4 tw-no-underline tw-text-gray-800 hover:tw-text-blue-600" onClick={openLoginModal}>
                            <span></span>&nbsp;&nbsp;<i className="fas fa-user"></i><span />
                          </NavLink>
                        </li>
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Uppdaterad login-skärm med <form> element */}
      {!isAuthorized && (
        <div className="tw-fixed tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-z-50">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-bg-white tw-p-8 tw-rounded-xl tw-shadow-2xl tw-w-[360px]">

            {/* Titel */}
            <h2 className="tw-text-2xl tw-font-bold tw-text-blue-800">
              Login
            </h2>
            {location.state?.reason === 'idle' && (
              <div className="tw-mb-3 tw-px-3 tw-py-2 tw-rounded tw-bg-yellow-100 tw-border tw-border-yellow-400 tw-text-yellow-800 tw-text-sm">
                Du loggades ut automatiskt efter 20 minuters inaktivitet.
              </div>
            )}

            {/* Inloggningsformulär med <form> element */}
            <form
              onSubmit={handleLogInOut}
              className="tw-w-full tw-flex tw-flex-col tw-gap-3 tw-bg-blue-50 tw-p-5 tw-rounded-lg tw-shadow-inner"
            >
              <input
                type="text"
                placeholder="Användarnamn"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="tw-w-full tw-px-4 tw-py-2 tw-rounded-lg tw-border tw-border-blue-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400"
              />

              <input
                type="password"
                placeholder="Lösenord"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="tw-w-full tw-px-4 tw-py-2 tw-rounded-lg tw-border tw-border-blue-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400"
              />

              <button
                type="submit"
                disabled={loading || !username.trim() || !password}
                className={`tw-w-full tw-px-6 tw-py-2 tw-bg-green-500 tw-text-white tw-rounded-lg tw-shadow
                  ${loading || !username.trim() || !password
                    ? 'tw-bg-green-400 tw-cursor-not-allowed'
                    : 'tw-bg-green-500 hover:tw-bg-green-600 focus:tw-ring-2 focus:tw-ring-green-400'
                  }`}
              >
                {loading ? 'Loggar in...' : 'Logga in'}
              </button>

              <div className="tw-h-[20px] tw-flex tw-items-center tw-justify-center">
                {captionText !== 'Logga in' && (
                  <p className="tw-text-sm tw-text-red-600 tw-text-center tw-m-0">
                    {captionText}
                  </p>
                )}
              </div>
            </form>

            {/* Separator */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-border-t tw-border-gray-300"></div>
              <span className="tw-text-gray-500 tw-text-sm">eller</span>
              <div className="tw-flex-1 tw-border-t tw-border-gray-300"></div>
            </div>

            {/* Public access knapp */}
            <button
              type="button"
              onClick={handleViewSchemas}
              className="tw-w-full tw-px-6 tw-py-2 tw-bg-green-500 tw-text-white tw-rounded-lg tw-shadow hover:tw-bg-green-600 tw-transition-all tw-font-medium"
            >
              <i className="fas fa-eye tw-mr-2"></i>
              Visa scheman
            </button>

            <p className="tw-text-xs tw-text-blue-500 tw-text-center">
              Som besökare får du endast läsbehörighet
            </p>

            <div className="tw-pt-4 tw-font-bold tw-whitespace-nowrap">
              © 2026 - Rolf Bive & Siréus Consulting AB
            </div>
          </div>
        </div>

      )}
      <div id="main-content">
        {children}
      </div>
      {!hideHeader && (
        <footer className="tw-text-center" style={{ backgroundColor: 'lightgrey', height: '81px', margin: '0' }}>
          <div className="tw-pt-4">
            © 2026 - Rolf Bive & Siréus Consulting AB
          </div>
        </footer>
      )}
      {showLoginModal && <LoginOutModalForm />}
    </div>
  );
}

export default Frame;